/**
 * Decomposing a kind-based association into one field per resolved target type.
 *
 * An association rule declares its target by KIND (`ahoy_contact_to_deal`,
 * target kind `case`), so a single rule resolves to every case-kind type in the
 * org — deals, tasks, and any custom case type. Rendered as one row it carries
 * one label for all of them, which is how a contact's tasks ended up listed
 * under "Associated Deals".
 *
 * The fix is to treat the rule as a UNION of per-type subsets: the record's
 * targets are decomposed by resolved entity type, each subset becomes its own
 * field (labelled with that type's plural label, picking only from that type),
 * and a write emits add/remove events SCOPED to the edited subset — never a
 * whole-set replacement. A subset a dedicated section owns (tasks) simply isn't
 * rendered — its reads and writes are otherwise identical, which is why hiding
 * is not a special case here.
 *
 * Both the read surface and the edit form build their fields from
 * `buildDetailFieldList`, so decomposing there keeps the two in agreement by
 * construction rather than by applying the same filter twice.
 */

import type { CrmObject } from '@/types/crm';

export interface ResolvedAssociationLike {
  association_type_name: string;
  /** The resolved target, or null when it could not be loaded. */
  object: CrmObject | null;
}

/** Resolved targets of one association type, grouped by target entity type. */
export type TargetSubsets = Map<string, CrmObject[]>;

/**
 * Group a record's resolved associations by association type, then by the
 * entity type each target actually resolved to.
 *
 * Unresolved targets (`object: null`) are skipped — there is nothing to render
 * for them and nothing to classify. They are never at risk on write either:
 * `diffTargetSubset` only ever touches ids it positively identifies as
 * belonging to the edited subset.
 */
export function decomposeAssociationTargets(
  associations: readonly ResolvedAssociationLike[]
): Map<string, TargetSubsets> {
  const byAssocType = new Map<string, TargetSubsets>();
  for (const assoc of associations) {
    if (!assoc.object) {
      continue;
    }
    const subsets =
      byAssocType.get(assoc.association_type_name) ??
      new Map<string, CrmObject[]>();
    const targetType = assoc.object.entity_type_name;
    const bucket = subsets.get(targetType) ?? [];
    bucket.push(assoc.object);
    subsets.set(targetType, bucket);
    byAssocType.set(assoc.association_type_name, subsets);
  }
  return byAssocType;
}

export interface SubsetDiffOptions {
  /**
   * The association's CURRENT target ids, across every type. Pass the freshest
   * set available — inside `useUpdateCrmAssociation` that is the re-fetched
   * object's associations, NOT the ids the field rendered from.
   */
  currentTargetIds: readonly string[];
  /** The entity type whose subset the user edited — the scope of this change. */
  targetEntityTypeName: string;
  /** The ids the user picked for that subset — its new contents. */
  nextTargetIds: readonly string[];
  /** Resolves a target id to its entity type. */
  resolveTargetEntityType: (entityId: string) => string | undefined;
  /**
   * Ids the caller KNOWS are in this subset, because its field rendered them.
   *
   * `resolveTargetEntityType` reads a client-side cache, so it can answer
   * `undefined` for an id that IS in scope — the entry was evicted between
   * render and save. Every such id would then be treated as out of scope: a
   * removal would silently no-op, and an id the user kept would look new and be
   * re-added. The ids a field rendered are not in doubt (a field renders one
   * subset of one type), so the caller states them and they bypass the lookup.
   *
   * Only ids OUTSIDE this set fall through to `resolveTargetEntityType`, which
   * preserves the concurrency property: a target someone else linked while the
   * field was open was never rendered here, so it stays out of scope and
   * untouched.
   */
  knownSubsetTargetIds?: readonly string[];
}

export interface SubsetDiff {
  added: string[];
  removed: string[];
}

/**
 * The add/remove events that express "this subset now contains exactly these".
 *
 * The write is a DELTA, never a whole-set replacement: only ids of
 * `targetEntityTypeName` can appear in `added` or `removed`. Targets of another
 * type — and any id whose type cannot be resolved — are out of scope by
 * construction, so they are neither removed nor needlessly re-asserted.
 *
 * That is what makes the change concurrency-safe without any coordination: a
 * target someone else linked to a DIFFERENT subset while this field was open is
 * untouched because it was never in scope, and one added to the SAME subset is
 * handled correctly because the diff runs against freshly-fetched state.
 */
export function diffTargetSubset({
  currentTargetIds,
  targetEntityTypeName,
  nextTargetIds,
  resolveTargetEntityType,
  knownSubsetTargetIds,
}: SubsetDiffOptions): SubsetDiff {
  const known = new Set(knownSubsetTargetIds ?? []);
  const currentInScope = currentTargetIds.filter(
    id => known.has(id) || resolveTargetEntityType(id) === targetEntityTypeName
  );
  const currentSet = new Set(currentInScope);
  const nextSet = new Set(nextTargetIds);

  return {
    added: [...new Set(nextTargetIds)].filter(id => !currentSet.has(id)),
    removed: currentInScope.filter(id => !nextSet.has(id)),
  };
}

/**
 * `any` is a WILDCARD target kind, not a kind an entity type declares. A rule
 * carrying it (`ahoy_audit_to_entity`) targets everything, so enumerating "the
 * types of kind any" yields nothing and enumerating "every type in the org"
 * would yield dozens of fields. Neither is a useful decomposition — such a rule
 * stays a single field.
 */
export const WILDCARD_TARGET_KIND = 'any';

/**
 * Target kinds whose rules stay ONE field, listing every type together.
 *
 * Decomposing is only worth it when the kind is an implementation detail the
 * user does not think in — a task and a deal are different things, so
 * "Associated Deals" holding both is wrong. Where the KIND is itself the user's
 * word for every type it covers, splitting invents a distinction they do not
 * make:
 *
 * - `any` — a wildcard; see {@link WILDCARD_TARGET_KIND}.
 * - `automation` — `ahoy_event_automation` and `ahoy_scheduled_automation` are
 *   both just "automations" to a user; how one is triggered is not a reason to
 *   split the row (product decision, 2026-08-27).
 *
 * Add a kind here when its types share one honest name, not to avoid churn.
 */
export const KINDS_RENDERED_WHOLE: ReadonlySet<string> = new Set([
  WILDCARD_TARGET_KIND,
  'automation',
]);

/**
 * The entity types a kind-based rule can resolve to in this org.
 *
 * Derived from the SCHEMA rather than from the record's existing targets, so a
 * type the record has none of still gets a field to add the first one through.
 * Kept general (matching on the rule's declared target kind) rather than
 * hardcoding `case` — every kind-based rule has the same shape.
 *
 * Honours the rule's `target_excluded_entity_type_names`: the backend has no
 * per-association exclusion filter for entity_kind queries, so a rule like
 * `ahoy_company_to_contact` (kind `person`, excluding `ahoy_user`) resolves to
 * `ahoy_contact` alone. Without this a company would grow a "Users" field for a
 * rule that explicitly refuses users.
 */
export function resolvableTargetEntityTypes(
  targetEntityKindName: string,
  objectTypes: readonly {
    entity_type_name: string;
    entity_kind?: string[];
  }[],
  excludedEntityTypeNames: readonly string[] = []
): string[] {
  if (targetEntityKindName === WILDCARD_TARGET_KIND) {
    return [];
  }
  const excluded = new Set(excludedEntityTypeNames);
  return objectTypes
    .filter(
      t =>
        (t.entity_kind ?? []).includes(targetEntityKindName) &&
        !excluded.has(t.entity_type_name)
    )
    .map(t => t.entity_type_name);
}
